import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';

import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { fmtDateTime } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { STATUS_LABELS } from './model';

// ExposePanel: Exposés zum Lead (am Lead und am Termin). Ein neues Exposé ist mit Lead, Objektaufnahme,
// Maklervertrag und dem Profil des Partners vorbelegt.
export function ExposePanel({ leadId, embedded = false }: { leadId: string; embedded?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: projectKeys.exposes(project.id, leadId), queryFn: ({ signal }) => papi.exposes(leadId, signal) });
  const create = useMutation({
    mutationFn: () => papi.createExpose(leadId, {}),
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.exposes(project.id, leadId) });
      navigate(`/p/${project.key}/expose/${d.id}`);
    },
  });
  return (
    <section className={embedded ? 'doc-area-row' : 'card'} aria-labelledby={`expose-${leadId}`}>
      <div className="section-head">
        {embedded ? <h3 id={`expose-${leadId}`}>Exposé</h3> : <h2 id={`expose-${leadId}`}>Exposé</h2>}
        {can('document.edit') && (
          <button
            type="button"
            className={embedded ? 'btn btn-small' : 'btn btn-primary btn-small'}
            disabled={create.isPending}
            onClick={() => create.mutate()}
          >
            Exposé erstellen
          </button>
        )}
      </div>
      {create.isError && <MutationError error={create.error} />}
      {list.isError && <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && list.data.items.length === 0 && <p className="muted">Noch kein Exposé. Angaben aus Objektaufnahme und Maklervertrag werden übernommen.</p>}
      {list.data && list.data.items.length > 0 && (
        <ul className="plain-list documents-list">
          {list.data.items.map((e) => (
            <li key={e.id}>
              <Link to={`/p/${project.key}/expose/${e.id}`}>
                {e.object_number} · {e.title || 'Exposé'}
              </Link>{' '}
              <span className={e.status === 'ENTWURF' ? 'badge badge-muted' : 'badge badge-ok'}>{STATUS_LABELS[e.status] ?? e.status}</span>
              <span className="muted">
                {' '}
                · {fmtDateTime(e.updated_at)}
                {e.broker_name ? ` · Partner ${e.broker_name}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
